/* eslint-disable max-lines */
import {
  Box,
  Grid,
  Pagination,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { includes, isEmpty, isNil, map, toLower } from 'lodash';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import { useGetPartnerAccountName } from 'common/utils';
import WorkQueues from 'enums/workQueue.enum';
import { workQueueActions } from 'store/slices/workQueueSlice';
import { upsertLineItemTags } from 'store/thunks/tagThunks';
import { listOrdersByPage, sortOrders } from 'store/thunks/workQueueThunks';
import OrderVerificationRow from './OrderVerificationRow';
import { TagsPopoverContent } from './WorkQueueTagsComponents';

const headCells = [
  {
    id: 'orderId',
    label: 'Order Id',
  },
  {
    id: 'PatientName',
    label: 'Patient',
  },
  {
    id: 'drugName',
    label: 'Drug Name',
  },
  {
    id: 'type',
    label: 'Type',
  },
  {
    id: 'quantity',
    label: 'Quantity',
  },
  {
    id: 'partnerName',
    label: 'Partner',
  },
  {
    id: 'partnerAccount',
    label: 'Partner Account',
  },
  {
    id: 'shipByDate',
    label: 'Ship By Date',
  },
  {
    id: 'requestNeedsByDate',
    label: 'Needs By Date',
  },
  {
    id: 'state',
    label: 'State Code',
  },
  {
    id: 'orderStatus',
    label: 'Status',
  },
  {
    id: 'createdAt',
    label: 'Created At',
  },
  {
    id: 'lastUpdated',
    label: 'Updated At',
  },
  {
    id: 'waitingOnFill',
    label: 'Waiting On Fill',
  },
  {
    id: 'totalInOrder',
    label: 'Total In Order',
  },
  {
    id: 'source',
    label: 'Source',
  },
  {
    id: 'tags',
    label: 'Tags',
  },
];

const sortableFields = [
  'shipByDate',
  'waitingOnFill',
  'orderId',
  'patientName',
  'createdAt',
  'lastUpdated',
  'totalInOrder',
  'partnerAccount',
];

const OrderVerificationTable = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
  const [selectedTagsLineItem, setSelectedTagsLineItem] = useState(null);
  const [loadingLineItems, setLoadingLineItems] = useState(new Set());

  const { sortBy, pages, currentPage } = useSelector(
    ({ workQueue }) => workQueue.orderVerification
  );

  const orderVerification = pages[currentPage];

  const totalPages = useSelector(({ workQueue }) =>
    Math.ceil(workQueue.orderVerification.count / 25)
  );

  const partnerAccountHandler = useGetPartnerAccountName();

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      sortOrders({
        sortBy: {
          sortKey,
          sortOrder: sortBy.sortKey === sortKey && sortBy.sortOrder === 'ASC' ? 'DESC' : 'ASC',
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      return;
    }

    dispatch(listOrdersByPage({ page }));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatTagText = (tag) => {
    const tagName = tag?.tagName || '';
    return tagName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleTagsClick = (event, lineItem) => {
    event.stopPropagation();
    setTagsAnchorEl(event.currentTarget);
    setSelectedTagsLineItem(lineItem);
  };

  const handleTagsClose = () => {
    setTagsAnchorEl(null);
    setSelectedTagsLineItem(null);
  };

  const handleRemoveTag = (lineItem, tagToRemove) => {
    const lineItemId = lineItem?.lineItemId;
    if (!lineItemId || loadingLineItems.has(lineItemId)) {
      return;
    }
    const currentTags = lineItem.tags || [];
    const updatedTags = currentTags.filter((tag) => tag.tagName !== tagToRemove?.tagName);
    setLoadingLineItems((prev) => new Set(prev).add(lineItemId));

    if (updatedTags.length === 0) {
      handleTagsClose();
    }

    dispatch(upsertLineItemTags({ lineItemId, currentTags, newTags: updatedTags }))
      .then(() => {
        dispatch(
          workQueueActions.updateLineItemTags({
            preset: WorkQueues.ORDER_VERIFICATION,
            page: currentPage,
            lineItemId,
            tags: updatedTags,
          })
        );
        if (updatedTags.length > 0) {
          setSelectedTagsLineItem((prev) => ({ ...prev, tags: updatedTags }));
        }
      })
      .finally(() => {
        setLoadingLineItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(lineItemId);
          return newSet;
        });
      });
  };

  const handleRemoveAllTags = (lineItem) => {
    setLoadingLineItems((prev) => new Set(prev).add(lineItem.lineItemId));
    handleTagsClose();

    const systemTagsToKeep = (lineItem.tags || []).filter((tag) => tag.isSystemTag);

    return dispatch(
      upsertLineItemTags({
        lineItemId: lineItem.lineItemId,
        currentTags: lineItem.tags || [],
        newTags: systemTagsToKeep,
      })
    )
      .then(() => {
        dispatch(
          workQueueActions.updateLineItemTags({
            preset: WorkQueues.ORDER_VERIFICATION,
            page: currentPage,
            lineItemId: lineItem.lineItemId,
            tags: systemTagsToKeep,
          })
        );
      })
      .finally(() => {
        setLoadingLineItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(lineItem.lineItemId);
          return newSet;
        });
      });
  };

  return (
    <Grid container direction='column'>
      <Popover
        open={Boolean(tagsAnchorEl)}
        anchorEl={tagsAnchorEl}
        onClose={handleTagsClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: '400px',
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
              mt: -1,
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: -5,
                left: 5,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '10px 10px 0 10px',
                borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
              },
            },
          },
        }}
      >
        {selectedTagsLineItem?.tags?.length > 0 && (
          <TagsPopoverContent
            selectedTagsLineItem={selectedTagsLineItem}
            loadingLineItems={loadingLineItems}
            formatTagText={formatTagText}
            handleRemoveTag={handleRemoveTag}
            handleRemoveAllTags={handleRemoveAllTags}
          />
        )}
      </Popover>

      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={headCell.id} align='center'>
                    {!includes(sortableFields, headCell.id) ? (
                      headCell.label
                    ) : (
                      <TableSortLabel
                        active={sortBy.sortKey === headCell.id}
                        direction={
                          sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'
                        }
                        onClick={handleSort}
                        data-head-property={headCell.id}
                      >
                        {headCell.label}
                        {sortBy.sortKey === headCell.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {map(orderVerification, (order) => (
                <OrderVerificationRow
                  key={order.lineItemId}
                  order={order}
                  partnerAccountHandler={partnerAccountHandler}
                  currentPage={currentPage}
                  loadingLineItems={loadingLineItems}
                  formatTagText={formatTagText}
                  handleTagsClick={handleTagsClick}
                />
              ))}
            </TableBody>

            {!isNil(orderVerification) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={20} align='right' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(orderVerification) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(orderVerification) && isEmpty(orderVerification) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default OrderVerificationTable;
